import React, { useEffect, useState } from 'react'
import _ from 'lodash'
import useTrans from '@hooks/useTrans'
import { cn, toCamelCase } from '@utils/safeTs'
import { ChatThreadItemType, FornitoreType, OrdineListType, PosizionePersonalizzazioneCarrelloLogType, ChatPayloadType } from '@utils/types'
import { Close } from '@components/icons'
import ChatLog  from '@components/molecules/ChatLog/ChatLog'
import ChatMessage, { ChatMessageProps } from '@components/molecules/ChatMessage/ChatMessage'
import AvatarGroup from '@components/molecules/AvatarGroup/AvatarGroup'
import Tabs from '@components/molecules/Tabs/Tabs'
import styles from './ChatDrawer.module.sass'
import WModal from '../../atoms/WModal/WModal'
import { Button, FormCheckbox, FormField, FormSelect, Loading, Modal } from '@components/atoms'
import ChatForm from '@components/molecules/ChatForm/ChatForm'
import { api } from '@utils/index'
import { MYACCOUNTCHAT, MYACCOUNTMESSAGGICHAT } from '@utils/endpoint'
import { useRouter } from 'next/router'

export interface ChatDrawerProps {
  open: boolean
  ordine?: OrdineListType
  onClose?: () => void 
  onMessageView?: (n: number) => void
  className?: string
}

const isLogItem = (item: ChatThreadItemType): boolean => item.type === 'log' || item.type === 'logOrdine'

const ChatDrawer = ({
  open,
  ordine,
  onClose,
  onMessageView,
  className,
}: ChatDrawerProps) => {

  const t = useTrans()
  const router = useRouter()

  const getOrdini = (o) => o.isParent ? o.ordini : [o]

  const [chat, setChat] = useState<ChatPayloadType>(undefined)
  const [mostraOriginali, setMostraOriginali] = useState(false)
  const [loading, setLoading] = useState(false)
  const [status, setStatus] = useState<number>(null)
  const fornitori: FornitoreType[] = React.useMemo(() => ordine ? _.uniqBy(getOrdini(ordine).map(o => o.fornitore).filter(x => !!x) || [], (x) => x.id) : [], [ordine])
  const [selectedFornitoreId, setSelectedFornitoreId] = useState(fornitori?.[0]?.id)
  const [confirmMessageRemove, setConfirmMessageRemove] = useState(undefined)

  // selectedChat: chat filtrata per fornitore
  const selectedChat = React.useMemo(() => ({
    messaggi: chat?.messaggi?.filter(x => (x.fornitoreId === selectedFornitoreId)) || [],
    log: chat?.log?.filter(x => !selectedFornitoreId || (x.fornitore?.id === selectedFornitoreId)) || [],
    logOrdine: chat?.logOrdine?.filter(x => !selectedFornitoreId || (x.fornitore?.id === selectedFornitoreId))
  }), [chat, selectedFornitoreId])
  const selectedUtenti = React.useMemo(() => _.uniqBy(chat?.messaggi?.map(x => x.utente) || [], (x) => x.id), [selectedChat.messaggi])

  const tabs = [
    // TODO - decommentare quando abiliteremo la chat
    // { id: 'chatdrawer_all', label: t("Tutto"), component: <></>, isVisible: () => !!selectedFornitoreId },
    // { id: 'chatdrawer_messages', label: t("Messaggi"), component: <></>, isVisible: () => !!selectedFornitoreId },
    { id: 'chatdrawer_log', label: t("Stati"), component: <></>, isVisible: () => true },
  ]

  const [activeTabId, setActiveTabId] = useState(tabs[0]?.id)

  const onMessageRemove = (item: ChatThreadItemType) => {
    setConfirmMessageRemove(undefined)
    api
      .delete(`${MYACCOUNTMESSAGGICHAT}${item.id}/`)
      .then((response) => {
        loadChat()
      })
      .catch((error) => {
        console.log(error)
      })
  }

  const renderThreadItem = (item: ChatThreadItemType) => {
    return isLogItem(item) 
      ? <ChatLog key={`chatlog_${item.id}`} item={item} level={item.type === 'log' ? 'posizione' : 'ordine'} mostraOriginali={mostraOriginali} />
      : <ChatMessage key={`chatmessage_${item.id}`} onRemove={() => setConfirmMessageRemove(item)} item={item} />
  }

  const loadChat = (showLoading = false) => {
    if (ordine) {
      if (showLoading)
        setLoading(true)
      api
        .get(`${MYACCOUNTCHAT}${ordine.id}/`, { params: { lang: router.locale } })
        .then((response) => {
          setChat(toCamelCase(response.data) as ChatPayloadType)
          if (showLoading)
            setLoading(false)
        })
        .catch((error) => {
          console.log(error)
          setStatus(404)
          if (showLoading)
            setLoading(false)
        })
    }
  }

  const onSubmit = (v, successCallback, errorCallback) => {
    const formData = new FormData()
    formData.append('messaggio', v.messaggio)
    formData.append('fornitore_id', String(selectedFornitoreId))
    formData.append('ordine_id', String(ordine.id))
    if (v.allegati?.length) {
      Array.from(v.allegati).forEach((file: File) => formData.append('allegati', file))
    }

    api
      .post(MYACCOUNTMESSAGGICHAT, formData)
      .then((response) => {
        if (successCallback){
          successCallback(response)
          loadChat()
        }
      })
      .catch((error) => {
        if (errorCallback)
          errorCallback(error)
      })
  }

  useEffect(() => {
    loadChat(true)
  }, [ordine])

  useEffect(() => setMostraOriginali(false), [ordine?.id])

  useEffect(() => {
    setSelectedFornitoreId(fornitori?.[0]?.id || null)
  }, [fornitori])

  useEffect(() => {
    setActiveTabId(tabs?.filter(x => x.isVisible())?.[0]?.id)
  }, [selectedChat])

  
  useEffect(() => {
    if (open){
      if (['chatdrawer_all', 'chatdrawer_messages'].includes(activeTabId) && !!selectedFornitoreId)
        // Se è aperta la chat e sto visualizzando una tab con i messaggi, setto come letti i mess dopo 2 secondi
        setTimeout(() => {
          api
            .post(MYACCOUNTMESSAGGICHAT + 'bulk_view/', { ordine_id: ordine.id, fornitore_id: selectedFornitoreId })
            .then((response) => onMessageView(0))
        }, 2000)
    }
  }, [open, activeTabId])

  return (
    <>
    <WModal
      id={'chatdrawer'}
      className={cn(styles.root, className)}
      open={open}
      ariaLabelledby={'chatdrawer_title'}
    >
      <header className={styles.header}>
        <h2 id={'chatdrawer_title'} className={styles.title}>
          {t("Log attività ordine" /* TODO - decommentare quando abiliteremo la chat (sostituire traduzione con "Chat") */)}
        </h2>
        <button
          type="button"
          className={styles.closeButton}
          onClick={onClose}
          aria-label={t("Chiudi chat")}
        >
          <Close aria-hidden />
        </button>
      </header>

      {loading ? (
        <Loading />
      ) : (
        <>
          <div className={styles.filters}>
            <div className={styles.fornitoriRow}>
              <FormField className={styles.fornitoriSelect}>
                <FormSelect
                  id={"chatdrawer_selectfornitori"}
                  value={selectedFornitoreId}
                  isDisabled={!fornitori?.length}
                  placeholder={fornitori?.length ? undefined : t("Nessun fornitore disponibile")}
                  options={fornitori?.length ? fornitori.map(s => ({
                    label: s.ragioneSociale,
                    value: s.id,
                  })) : []}
                  onChange={(e) => setSelectedFornitoreId(e.target.value)}
                />
              </FormField>
              <div className={styles.participantsWrap}>
                <AvatarGroup 
                  id="chatdrawer_participants" 
                  utenti={selectedUtenti} 
                  ariaLabel={t("Partecipanti alla chat")} 
                  className={styles.participants} 
                  dialog
                />
              </div>
            </div>

            {selectedChat.log.some(log => log.noteTradotta && log.noteTradotta !== log.note) && (
              <FormCheckbox
                id={`chat-drawer-traduzioni-${ordine.id}`}
                size="sm"
                label={
                  <>
                    {t('Mostra testi originali')}
                    {' — '}
                    {t('Traduzione automatica generata da IA')}
                  </>
                }
                checked={mostraOriginali}
                onChange={(event) => setMostraOriginali(event.target.checked)}
                className={styles.translationToggle}
              />
            )}

            {/* TODO - decommentare quando abiliteremo la chat (senza chat c'è un solo tab, non lo mostro) */}
            {/* {!!tabs.filter(t => t.isVisible())?.length && (
              <div className={styles.tabWrap}>
                <Tabs
                  tabs={tabs.filter(t => t.isVisible())}
                  size="sm"
                  activeId={activeTabId}
                  onTabSelect={(tabId) => setActiveTabId(tabId)}
                />
              </div>
            )} */}
          </div>

          {(!!selectedChat?.messaggi?.length || !!selectedChat?.log?.length || !!selectedChat?.logOrdine?.length) && (
            activeTabId === 'chatdrawer_all' 
              || (activeTabId === 'chatdrawer_messages' && selectedChat?.messaggi?.length) 
              || (activeTabId === 'chatdrawer_log' && selectedChat?.log?.length || !!selectedChat?.logOrdine?.length)
            ) ? (
              <div className={styles.list} role="list">
                {(selectedChat?.messaggi || []).map(x => ({...x, type: 'messaggio' as 'log' | 'logOrdine' | 'messaggio' }))
                  .concat((selectedChat?.log || []).map(x => ({...x, type: 'log' as 'log' | 'logOrdine' | 'messaggio'})))
                  .concat((selectedChat?.logOrdine || []).map(x => ({...x, type: 'logOrdine' as 'log' | 'logOrdine' | 'messaggio'})))
                  .sort((a, b) => a.created < b.created ? 1 : -1)
                  .filter(item => 
                    activeTabId === 'chatdrawer_all' 
                    || (activeTabId === 'chatdrawer_messages' && !isLogItem(item)) 
                    || (activeTabId === 'chatdrawer_log' && isLogItem(item))
                  ).map((item) => renderThreadItem(item))}
              </div>
          ) : (
            <div className={styles.empty}>
              {t("Nessun elemento")}
            </div>
          )}
          {/* TODO - decommentare quando abiliteremo la chat */}
          {/* {!!selectedFornitoreId && <ChatForm onSubmit={onSubmit} />} */}
        </>
      )}
    </WModal>
    <Modal
      id={"chatdrawer_removemessageconfirm_modal"}
      open={!!confirmMessageRemove}
      onClose={() => setConfirmMessageRemove(undefined)}
      title={t("Confema rimozione")}
      buttons={(
        <>
          <Button label={t("Annulla")} variant='secondary' onClick={() => setConfirmMessageRemove(undefined)} />
          <Button label={t("Conferma")} variant='primary' onClick={() => onMessageRemove(confirmMessageRemove)} />
        </>
      )}
      className={styles.confirmMessageRemoveModal}
    >
      <p className={styles.confirmMessageRemoveModalDescription}>{t("Sei sicuro di voler eliminare il messaggio?")}</p>
    </Modal>
    </>
  )
}

export default ChatDrawer
